import React, {createContext, useCallback, useContext, useEffect, useMemo, useState} from "react";
import {Link} from "../types/Link";
import Modal from "../components/Modal";
import {toast, ToastContainer, ToastPosition, TypeOptions} from "react-toastify";
import ApiService from "../services/ApiService";
import {useFetchSingle} from "../hooks/useFetchSingle";
import {PluginData} from "../types/PluginData";
import i18n from "../i18n";

type sizes = 'lg'

type modalData = {
    heading?: React.ReactNode,
    content: React.ReactNode,
    visible: boolean
    fullscreen: boolean
    width?: string,
    size?: sizes
}

type GlobalContextType = {
    activeTab: Link;
    setActiveTab: React.Dispatch<React.SetStateAction<Link>>;
    showModal: (data: {heading?: React.ReactNode, content: React.ReactNode, fullscreen: boolean, size?: sizes}) => void;
    closeModal: () => void;
    showToast: (
        position: ToastPosition,
        type: 'info' | 'success' | 'warning' | 'error' | 'default',
        message: string
    ) => void;
    modal: modalData;
    fullscreen: boolean;
    setFullscreen: React.Dispatch<React.SetStateAction<boolean>>;
    api: ApiService;
    plugin: PluginData | null
    isLoading: boolean
}

const GlobalContext = createContext<GlobalContextType|undefined>(undefined)

type GlobalContextProps = {
    children: React.ReactNode|React.ReactNode[],
}

export const GlobalContextProvider = ({children}: GlobalContextProps): JSX.Element => {
    const api = useMemo(() => {
        return new ApiService();
    }, [])

    const [fullscreen, setFullscreen] = useState<boolean>(false)

    const [activeTab, setActiveTab] = useState<Link>({
        number: parseInt(localStorage.getItem('zrCustomizerTab') ?? '1'),
        name: 'Overview',
    })

    const [modal, setModal] = useState<modalData>({
        content: '',
        visible: false,
        fullscreen: false
    })

    const showToast = (position: ToastPosition, type: TypeOptions, message: string) => {
        toast.success(message, {
            position: position,
            type : type,
        });
    }

    const showModal = useCallback((data: {
        heading?: React.ReactNode,
        content: React.ReactNode,
        fullscreen: boolean, width?: string, size?: sizes
    }) => {
        setModal((prevModal) => ({
            ...prevModal,
            visible: true,
            heading: data.heading,
            content: data.content,
            fullscreen: data.fullscreen,
            width: data.width,
            size: data.size
        }))
    }, [])

    const closeModal = useCallback(() => {
        setModal((prevModal) => ({
            ...prevModal,
            visible: false,
        }))
    }, [])

    useEffect(() => {
        localStorage.setItem('zrCustomizerTab', String(activeTab.number))
    }, [activeTab])

    const {
        data: plugin,
        isLoading,
        fetch: fetchPlugin,
    } = useFetchSingle<PluginData>('plugin')

    useEffect(() => {
        if (fetchPlugin) fetchPlugin(window.location.href.includes('admin') ? 'admin' : 'frontend')
    }, [fetchPlugin]);

    useEffect(() => {
        if (!plugin) return
        i18n.changeLanguage(plugin.language.i18n).then(() => {
            localStorage.setItem('language', plugin.language.i18n)
        })
    }, [plugin, i18n])

    return (
        <GlobalContext.Provider value={{
            api,
            activeTab,
            setActiveTab,
            showModal,
            closeModal,
            modal,
            showToast,
            fullscreen,
            setFullscreen,
            plugin,
            isLoading
        }}>
            <Modal/>
            {children}
            <ToastContainer/>
        </GlobalContext.Provider>
    );
}

export const useGlobalContext = () => {
    const data = useContext(GlobalContext)

    if (data === undefined) {
        throw new Error('useGlobalContext must used within GlobalContextProvider')
    }

    return data
}

